import { useQuery } from '@tanstack/react-query'
import { useAtomValue } from 'jotai'
import GlobalLogger from '@shared/utils/logger/GlobalLogger'

import { useApi } from '../api'
import { chatUsageSchema, type ChatUsage } from '../engine/events'
import { describeCause } from '../engine/turn-trace'
import { useGetRealChatId } from '../store/chats'
import { liveChatUsageAtom } from '../store/chat-usage'
import { useLocalChatIds } from '../store/local-chat-ids'
import { useIsTurnLive } from '../store/turn-status'
import { pickFreshestUsage, shouldFetchChatUsage } from '../utils/chat-usage'

const log = GlobalLogger.bind('ChatUsage')

// Short enough that turns run in another window or on another device show up
// on the next focus, long enough that flipping between chats doesn't refetch.
const USAGE_STALE_TIME_MS = 30_000

/**
 * A chat's credit spend and context fill: the fresher of this window's live
 * stream totals and a fetched snapshot. Undefined until one exists, and after
 * a failed fetch.
 */
export function useChatUsage(chatId: string | undefined): ChatUsage | undefined {
  const { get } = useApi()
  const getRealChatId = useGetRealChatId()
  const realChatId = chatId ? getRealChatId(chatId) : undefined
  const live = useAtomValue(liveChatUsageAtom)[realChatId ?? '']
  const localChatIds = useLocalChatIds()
  const isTurnLive = useIsTurnLive(realChatId)
  // Gates the fetch only; the cached snapshot stays readable while a turn streams.
  const canFetch = shouldFetchChatUsage(realChatId, { localChatIds, isTurnLive })

  const { data: fetched } = useQuery({
    queryKey: ['chat-usage', realChatId],
    queryFn: async () => {
      try {
        const resp = await get(`conversations/${realChatId}/usage`, { version: 'v1' })
        if (!resp.ok) {
          throw new Error(`status ${resp.status}`)
        }
        const json = await resp.json()
        return chatUsageSchema.parse(json.data)
      } catch (cause) {
        log.warn(`usage fetch failed chat=${realChatId}: ${describeCause(cause)}`)
        throw cause
      }
    },
    enabled: canFetch,
    staleTime: USAGE_STALE_TIME_MS,
    refetchOnWindowFocus: true,
    retry: false,
  })

  return pickFreshestUsage(live, fetched)
}
